import React, { useState } from 'react';
import { db, auth } from '../../firebase';
import { collection, doc, getDocs, writeBatch } from 'firebase/firestore';
import Papa from 'papaparse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import WaButton from '../../components/wa/WaButton';

// Columns we expect in a real MyMusicStaff student export. If none of these are
// present the uploaded file almost certainly isn't an MMS roster and we refuse to
// import (avoids the "N blank students" garbage-import path).
const EXPECTED_MMS_COLUMNS = ['My Music Staff Student ID', 'First Name', 'Last Name'];

const ImportStudents = () => {
	const [parsedData, setParsedData] = useState(null);
	const [parseWarnings, setParseWarnings] = useState([]);
	const [analysis, setAnalysis] = useState(null); // pre-write summary
	const [analyzing, setAnalyzing] = useState(false);
	const [importing, setImporting] = useState(false);
	const [progress, setProgress] = useState(0);
	const [results, setResults] = useState(null);
	const [error, setError] = useState('');

	const resetForFile = () => {
		setParsedData(null);
		setParseWarnings([]);
		setAnalysis(null);
		setResults(null);
		setProgress(0);
	};

	const handleFileChange = (e) => {
		const selectedFile = e.target.files[0];
		resetForFile();
		setError('');
		if (!selectedFile) return;

		// Papa parses CSV only. An Excel (.xlsx/.xls) or other binary file yields
		// garbage rows that were previously reported as real students, so reject it
		// up front by extension before we ever hand it to Papa.
		const name = (selectedFile.name || '').toLowerCase();
		if (!name.endsWith('.csv')) {
			setError(
				'This importer reads CSV files only. In MyMusicStaff choose "Download" and pick the ' +
				'CSV option (not Excel), then upload that .csv file here.'
			);
			return;
		}

		Papa.parse(selectedFile, {
			header: true,
			skipEmptyLines: true,
			complete: (result) => {
				const fields = result.meta?.fields || [];
				const hasExpected = EXPECTED_MMS_COLUMNS.some((c) => fields.includes(c));
				if (!hasExpected) {
					setError(
						'That CSV is missing the expected MyMusicStaff columns (e.g. "First Name", ' +
						'"Last Name", "My Music Staff Student ID"). Please upload an unmodified MMS ' +
						'student export.'
					);
					return;
				}

				const rows = result.data || [];
				if (rows.length === 0) {
					setError('No student rows were found in that CSV.');
					return;
				}

				// Surface Papa's own parse errors instead of importing partial garbage.
				const errs = result.errors || [];
				if (errs.length > 0) {
					const preview = errs.slice(0, 5).map((er) => {
						const where = typeof er.row === 'number' ? `row ${er.row + 2}` : 'file';
						return `${where}: ${er.message}`;
					});
					setParseWarnings([
						`${errs.length} row${errs.length === 1 ? '' : 's'} could not be read cleanly.`,
						...preview,
						...(errs.length > 5 ? [`…and ${errs.length - 5} more`] : []),
					]);
				}

				setParsedData(rows);
			},
			error: (err) => {
				setError('Failed to parse CSV: ' + err.message);
			},
		});
	};

	const parseMMSDate = (dateStr) => {
		if (!dateStr) return null;
		try {
			// MMS format: "10/7/2025 5:00:00 PM"
			const date = new Date(dateStr);
			return date.toISOString();
		} catch (e) {
			return null;
		}
	};

	// Build the Firestore doc id + payload for a CSV row. Returns null if the row
	// can't be processed (bubbled up as a row error).
	const buildRecord = (row) => {
		const studentId = row['My Music Staff Student ID'] || `student_${Date.now()}_${Math.random()}`;

		const studentData = {
			// Basic Info
			firstName: row['First Name'] || '',
			lastName: row['Last Name'] || '',
			fullName: `${row['First Name'] || ''} ${row['Last Name'] || ''}`.trim(),
			email: row['Email'] || '',
			phone: row['Mobile Phone'] || '',

			// Student Details
			birthday: row['Birthday'] || '',
			age: parseInt(row['Age']) || null,
			school: row['School'] || '',
			instrument: row['Instrument'] || 'Voice',
			level: row['Level'] || '',
			status: row['Status'] || 'Active',

			// Dates
			dateStarted: row['Date Started'] || '',
			inactiveDate: row['Inactive Date'] || '',
			lastLesson: parseMMSDate(row['Last Lesson']),
			nextLesson: parseMMSDate(row['Next Lesson']),

			// Lesson Info
			defaultLessonLength: parseInt(row['Default Lesson Length']) || 45,
			billingType: row['Billing Type'] || 'FixedMonthly',
			// TWO MMS COLUMNS ARE DELIBERATELY NOT IMPORTED. Both map to fields that
			// have been retired from the student form and profile, and this importer
			// is the one path that could repopulate either one WHOLESALE — a single
			// CSV run would undo the retirement silently.
			//
			//   "Make-up Credits" -> students.makeupCredits. Anthony 2026-07-14: MMS
			//   credits do not carry into fall, the ledger starts at zero for
			//   everyone, so importing them contradicted the decision directly.
			//   Credits live in the makeupCredits COLLECTION + students.makeupBalance.
			//
			//   "Price" -> students.price. No billing code reads it (tuition resolves
			//   from src/canon/season.json and is stamped onto the invoice). The
			//   mapping also carried a hardcoded `|| '$220.00'` fallback — last
			//   season's installment — so a blank column INVENTED a stale rate rather
			//   than leaving the field empty. Retired 2026-08-09 (Anthony).

			// Parent Contact 1
			parent1: {
				firstName: row['Parent Contact 1 First Name'] || '',
				lastName: row['Parent Contact 1 Last Name'] || '',
				email: row['Parent Contact 1 Email'] || '',
				phone: row['Parent Contact 1 Mobile Phone'] || row['Parent Contact 1 Home Phone'] || '',
				address: row['Parent Contact 1 Address'] || row['Address'] || '',
			},

			// Parent Contact 2 (if exists)
			parent2: row['Parent Contact 2 First Name'] ? {
				firstName: row['Parent Contact 2 First Name'] || '',
				lastName: row['Parent Contact 2 Last Name'] || '',
				email: row['Parent Contact 2 Email'] || '',
				phone: row['Parent Contact 2 Mobile Phone'] || row['Parent Contact 2 Home Phone'] || '',
				address: row['Parent Contact 2 Address'] || '',
			} : null,

			// Additional Info
			notes: row['Note'] || '',
			referrer: row['Referrer'] || '',
			groupTags: row['Group Tags'] ? row['Group Tags'].split(',').map(t => t.trim()) : [],

			// System fields
			importedFrom: 'MyMusicStaff',
			importedAt: new Date().toISOString(),
			importedBy: auth.currentUser?.uid || 'system',
			mmsStudentId: row['My Music Staff Student ID'] || '',
			mmsFamilyId: row['My Music Staff Family ID'] || '',
		};

		return { studentId, studentData, displayName: studentData.fullName || studentId };
	};

	// Pre-write analysis: read the existing students once, classify each row as
	// new / update / skipped-protected, and surface the counts BEFORE any write.
	const analyzeImport = async () => {
		if (!parsedData || parsedData.length === 0) {
			setError('No data to import');
			return;
		}
		setAnalyzing(true);
		setError('');
		try {
			const snap = await getDocs(collection(db, 'students'));
			const existing = new Map();
			snap.forEach((d) => existing.set(d.id, d.data()));

			const toCreate = [];
			const toUpdate = [];
			const skippedProtected = [];
			const rowErrors = [];

			for (const row of parsedData) {
				try {
					const rec = buildRecord(row);
					const current = existing.get(rec.studentId);
					if (current && (current.locked === true || current.isTestStudent === true)) {
						skippedProtected.push({
							name: rec.displayName,
							reason: current.locked === true ? 'locked' : 'test student',
						});
					} else if (current) {
						toUpdate.push(rec);
					} else {
						toCreate.push(rec);
					}
				} catch (rowError) {
					rowErrors.push({
						student: `${row['First Name'] || ''} ${row['Last Name'] || ''}`.trim() || '(unnamed row)',
						error: rowError.message,
					});
				}
			}

			setAnalysis({ toCreate, toUpdate, skippedProtected, rowErrors });
		} catch (err) {
			console.error('Import analysis error:', err);
			setError('Could not read existing students to compare against: ' + err.message);
		} finally {
			setAnalyzing(false);
		}
	};

	// Commit only after the user confirms the pre-write summary. New docs are
	// written plainly; existing docs use merge:true so a re-import never wipes
	// fields the CSV doesn't carry (protected docs were already filtered out).
	const commitImport = async () => {
		if (!analysis) return;
		const writes = [
			...analysis.toCreate.map((rec) => ({ rec, merge: false })),
			...analysis.toUpdate.map((rec) => ({ rec, merge: true })),
		];

		setImporting(true);
		setError('');
		setProgress(0);

		const importResults = {
			created: 0,
			updated: 0,
			skippedProtected: analysis.skippedProtected.length,
			failed: analysis.rowErrors.length,
			errors: [...analysis.rowErrors],
		};

		try {
			const batchSize = 500;
			for (let i = 0; i < writes.length; i += batchSize) {
				const batch = writeBatch(db);
				const chunk = writes.slice(i, i + batchSize);
				for (const { rec, merge } of chunk) {
					const studentRef = doc(collection(db, 'students'), rec.studentId);
					batch.set(studentRef, rec.studentData, merge ? { merge: true } : undefined);
					if (merge) importResults.updated++; else importResults.created++;
				}
				await batch.commit();
				const currentProgress = writes.length === 0
					? 100
					: Math.min(100, ((i + chunk.length) / writes.length) * 100);
				setProgress(currentProgress);
			}
			setProgress(100);
			setResults(importResults);
		} catch (err) {
			console.error('Import error:', err);
			setError('Import failed partway through: ' + err.message + '. Some records may have been written; re-run to finish (updates are safe to repeat).');
		} finally {
			setImporting(false);
		}
	};

	const summaryReady = analysis && !results;
	const writeCount = analysis ? analysis.toCreate.length + analysis.toUpdate.length : 0;

	return (
		<div className="container py-5">
			<h1 className="mb-4" style={{ color: '#384971' }}>
				Import Students from MyMusicStaff
			</h1>

			<wa-card className="mb-4">
				<div slot="header">
					<strong>Step 1: Export from MyMusicStaff</strong>
				</div>
				<ol>
					<li>Go to MyMusicStaff → Students</li>
					<li>Click "Options" → "Download"</li>
					<li>Choose <strong>CSV</strong> format (not Excel)</li>
					<li>Save the file</li>
				</ol>
			</wa-card>

			<wa-card className="mb-4">
				<div slot="header">
					<strong>Step 2: Upload CSV File</strong>
				</div>
				<div>
					{error && (
						<wa-callout variant="danger" className="mb-3">
							{error}
						</wa-callout>
					)}

					<input
						type="file"
						accept=".csv,text/csv"
						onChange={handleFileChange}
						className="form-control mb-3"
						style={{
							padding: '0.5rem',
							border: '1px solid var(--wa-color-neutral-300)',
							borderRadius: 'var(--wa-border-radius-medium)'
						}}
					/>

					{parseWarnings.length > 0 && (
						<wa-callout variant="warning" className="mb-3">
							<strong><FontAwesomeIcon icon={faTriangleExclamation} className="me-1" /> Some rows had parse issues</strong>
							<ul className="mb-0 mt-2">
								{parseWarnings.map((w, idx) => <li key={idx}>{w}</li>)}
							</ul>
						</wa-callout>
					)}

					{parsedData && (
						<wa-callout variant="success">
							<FontAwesomeIcon icon={faCircleCheck} className="me-1" /> File loaded: {parsedData.length} rows found
						</wa-callout>
					)}
				</div>
			</wa-card>

			{parsedData && !results && (
				<wa-card className="mb-4">
					<div slot="header">
						<strong>Step 3: Preview Data</strong>
					</div>
					<div>
						<p className="text-muted">
							Showing first 5 rows. Review before importing.
						</p>
						<div style={{ maxHeight: '400px', overflow: 'auto' }}>
							<table className="table table-striped table-bordered table-sm">
								<thead>
									<tr>
										<th>Name</th>
										<th>Email</th>
										<th>Parent</th>
										<th>School</th>
										<th>Status</th>
									</tr>
								</thead>
								<tbody>
									{parsedData.slice(0, 5).map((row, idx) => (
										<tr key={idx}>
											<td>{row['First Name']} {row['Last Name']}</td>
											<td>{row['Email'] || <span className="text-muted">—</span>}</td>
											<td>
												{row['Parent Contact 1 First Name']} {row['Parent Contact 1 Last Name']}
												{row['Parent Contact 1 Email'] && (
													<><br /><small className="text-muted">{row['Parent Contact 1 Email']}</small></>
												)}
											</td>
											<td>{row['School'] || <span className="text-muted">—</span>}</td>
											<td>
												<wa-badge variant={row['Status'] === 'Active' ? 'success' : 'neutral'}>
													{row['Status']}
												</wa-badge>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						{parsedData.length > 5 && (
							<p className="text-muted small">
								...and {parsedData.length - 5} more rows
							</p>
						)}
					</div>
				</wa-card>
			)}

			{parsedData && !analysis && !results && (
				<wa-card className="mb-4">
					<div slot="header">
						<strong>Step 4: Review Import</strong>
					</div>
					<div>
						<p className="text-muted mb-3">
							We'll compare this file against your existing students and show you exactly
							what will change before anything is written. Locked and test students are
							never overwritten.
						</p>

						<WaButton
							variant="primary"
							size="large"
							onClick={analyzeImport}
							disabled={analyzing}
						>
							{analyzing ? (
								<>
									<wa-spinner slot="prefix"></wa-spinner>
									Analyzing...
								</>
							) : (
								'Review changes'
							)}
						</WaButton>
					</div>
				</wa-card>
			)}

			{summaryReady && (
				<wa-card className="mb-4">
					<div slot="header">
						<strong>Step 5: Confirm Import</strong>
					</div>
					<div>
						<p className="text-muted mb-3">
							Here's what will happen. Nothing has been written yet.
						</p>
						<ul>
							<li><strong>{analysis.toCreate.length}</strong> new student{analysis.toCreate.length === 1 ? '' : 's'} to add</li>
							<li><strong>{analysis.toUpdate.length}</strong> existing student{analysis.toUpdate.length === 1 ? '' : 's'} to update (other fields preserved)</li>
							<li><strong>{analysis.skippedProtected.length}</strong> protected (locked / test) student{analysis.skippedProtected.length === 1 ? '' : 's'} skipped — left untouched</li>
							{analysis.rowErrors.length > 0 && (
								<li className="text-danger"><strong>{analysis.rowErrors.length}</strong> row{analysis.rowErrors.length === 1 ? '' : 's'} could not be processed</li>
							)}
						</ul>

						{analysis.skippedProtected.length > 0 && (
							<wa-callout variant="neutral" className="mb-3">
								<strong>Skipped (protected):</strong>
								<ul className="mb-0 mt-1">
									{analysis.skippedProtected.slice(0, 10).map((s, idx) => (
										<li key={idx}>{s.name} <span className="text-muted">({s.reason})</span></li>
									))}
									{analysis.skippedProtected.length > 10 && (
										<li className="text-muted">…and {analysis.skippedProtected.length - 10} more</li>
									)}
								</ul>
							</wa-callout>
						)}

						{importing && (
							<div className="mb-3">
								<p>Importing... {Math.round(progress)}%</p>
								<wa-progress-bar value={progress}></wa-progress-bar>
							</div>
						)}

						<div className="d-flex gap-2 flex-wrap">
							<WaButton
								variant="primary"
								size="large"
								onClick={commitImport}
								disabled={importing || writeCount === 0}
							>
								{importing ? (
									<>
										<wa-spinner slot="prefix"></wa-spinner>
										Importing...
									</>
								) : (
									writeCount === 0 ? 'Nothing to import' : `Confirm & import ${writeCount} record${writeCount === 1 ? '' : 's'}`
								)}
							</WaButton>
							<WaButton
								variant="neutral"
								size="large"
								appearance="outlined"
								onClick={() => setAnalysis(null)}
								disabled={importing}
							>
								Back
							</WaButton>
						</div>
					</div>
				</wa-card>
			)}

			{results && (
				<wa-card>
					<div slot="header">
						<strong>Import Complete</strong>
					</div>
					<div>
						<wa-callout variant="success">
							<h5 className="mb-2"><FontAwesomeIcon icon={faCircleCheck} className="me-1" /> {results.created} added · {results.updated} updated</h5>
							<p className="mb-0 text-muted">
								{results.skippedProtected} protected student{results.skippedProtected === 1 ? '' : 's'} skipped.
							</p>
							{results.failed > 0 && (
								<p className="mb-0 text-danger">
									<FontAwesomeIcon icon={faTriangleExclamation} className="me-1" /> {results.failed} row{results.failed === 1 ? '' : 's'} failed to import
								</p>
							)}
						</wa-callout>

						{results.errors.length > 0 && (
							<div className="mt-4">
								<h6>Errors:</h6>
								<ul>
									{results.errors.map((err, idx) => (
										<li key={idx}>
											<strong>{err.student}:</strong> {err.error}
										</li>
									))}
								</ul>
							</div>
						)}

						<wa-button
							variant="primary"
							href="/portal/work/students"
							className="mt-3"
						>
							View Students →
						</wa-button>
					</div>
				</wa-card>
			)}
		</div>
	);
};

export default ImportStudents;
